/**
 * Authoring helpers for level geometry.
 *
 * A level declares what is solid in metres and degrees, never in engine types. Every helper
 * returns the same `ColliderSpec`, so a wall, a crate and a ramp differ by their numbers rather
 * than by their kind, and nothing downstream has to switch on what a solid is.
 *
 * `impactSurface` has no default anywhere a level author can reach. What a round finds when it
 * lands is not something to inherit by accident: a silent fallback would spark concrete off a
 * timber crate and nothing would catch it.
 */

import type { ImpactSurfaceId } from '@/sim/impacts/impactSurfaceContract';
import { fieldRange } from '@/sim/physics/heightSampling';
import { degToRad } from '@/sim/math';
import type { ColliderSpec, HeightSamples } from '@/sim/physics/physicsContract';
import { vec3 } from '@/sim/vec3';
import type { Vec3 } from '@/sim/vec3';

export interface BoxOptions {
  readonly id: string;
  readonly center: Vec3;
  readonly halfExtents: Vec3;
  /** What a round finds here. Null for a bound that pens the player in and lets bullets past. */
  readonly impactSurface: ImpactSurfaceId | null;
  readonly yawDegrees?: number;
  readonly tiltDegrees?: number;
  readonly visible?: boolean;
}

/** A solid box. The general case every other helper narrows. */
export function boxCollider(options: BoxOptions): ColliderSpec {
  return {
    id: options.id,
    shape: 'box',
    center: options.center,
    halfExtents: options.halfExtents,
    yaw: degToRad(options.yawDegrees ?? 0),
    tilt: degToRad(options.tiltDegrees ?? 0),
    visible: options.visible ?? true,
    impactSurface: options.impactSurface,
  };
}

export interface WallOptions {
  readonly id: string;
  /** Centre of the wall face at ground level. */
  readonly center: Vec3;
  readonly impactSurface: ImpactSurfaceId | null;
  readonly halfLength: number;
  readonly height: number;
  readonly thickness: number;
  readonly yawDegrees?: number;
  readonly visible?: boolean;
}

/**
 * A wall standing on the ground, given its footprint rather than its centre height. Level authors
 * think in "three metres tall from here", not in half extents about a midpoint.
 */
export function wallCollider(options: WallOptions): ColliderSpec {
  return boxCollider({
    id: options.id,
    center: vec3(options.center.x, options.center.y + options.height / 2, options.center.z),
    halfExtents: vec3(options.halfLength, options.height / 2, options.thickness / 2),
    impactSurface: options.impactSurface,
    yawDegrees: options.yawDegrees ?? 0,
    visible: options.visible ?? true,
  });
}

export interface CylinderOptions {
  readonly id: string;
  /** Centre of the base, on whatever it stands on, the way a post is placed rather than centred. */
  readonly base: Vec3;
  readonly impactSurface: ImpactSurfaceId | null;
  readonly radius: number;
  readonly height: number;
  readonly visible?: boolean;
}

/** An upright cylinder: a post, a drum, a pipe. Never leans, so it takes no tilt. */
export function cylinderCollider(options: CylinderOptions): ColliderSpec {
  return {
    id: options.id,
    shape: 'cylinder',
    center: vec3(options.base.x, options.base.y + options.height / 2, options.base.z),
    halfExtents: vec3(options.radius, options.height / 2, options.radius),
    yaw: 0,
    tilt: 0,
    visible: options.visible ?? true,
    impactSurface: options.impactSurface,
  };
}

export interface RampOptions {
  readonly id: string;
  /** Where the ramp meets the ground, at the centre of its low edge. */
  readonly foot: Vec3;
  readonly impactSurface: ImpactSurfaceId | null;
  readonly halfWidth: number;
  /** Along-slope length, so a steeper ramp of the same length climbs higher. */
  readonly length: number;
  readonly thickness: number;
  readonly angleDegrees: number;
  readonly yawDegrees?: number;
}

/**
 * A tilted slab climbing away from its foot toward -Z before yaw, which is downrange. Authored by
 * angle, because the slope limit is what a ramp exists to test and a rise-over-run would hide it.
 */
export function rampCollider(options: RampOptions): ColliderSpec {
  const angle = degToRad(options.angleDegrees);
  const half = options.length / 2;
  const yaw = degToRad(options.yawDegrees ?? 0);

  // Centre sits half a length up the slope from the foot and half a slab under the surface, so the
  // walking surface, not the middle of the slab, passes through the foot. The drop is the half
  // thickness measured vertically through a tilted slab, which is `/ cos` and not `* cos`: at the
  // angles a ramp is authored at the two are close enough to look right and far enough apart to
  // leave a lip at the toe taller than the step the character can take.
  const alongZ = -half * Math.cos(angle);
  const rise = half * Math.sin(angle);
  const lift = -(options.thickness / 2) / Math.cos(angle);

  return {
    id: options.id,
    shape: 'box',
    center: vec3(
      options.foot.x + alongZ * Math.sin(yaw),
      options.foot.y + rise + lift,
      options.foot.z + alongZ * Math.cos(yaw),
    ),
    halfExtents: vec3(options.halfWidth, options.thickness / 2, half),
    yaw,
    tilt: angle,
    visible: true,
    impactSurface: options.impactSurface,
  };
}

export interface HeightfieldOptions {
  readonly id: string;
  /** The middle of the footprint, at the height the samples are measured from. */
  readonly center: Vec3;
  /** Half the footprint on X and Z. The Y half extent is derived from the samples themselves. */
  readonly halfWidth: number;
  readonly halfDepth: number;
  readonly samples: HeightSamples;
  readonly impactSurface: ImpactSurfaceId | null;
  readonly visible?: boolean;
}

/**
 * A sampled ground surface: camber, settlement, a slumped crater.
 *
 * Never yawed and never tilted, which is why this helper takes neither. A heightfield is the
 * ground, and ground does not turn; every reader outside the physics engine samples it in world
 * space on that promise. See [heightSampling](./heightSampling.ts).
 *
 * The vertical half extent is measured off the samples rather than asked for, so the bounding box
 * everything culls against is the one the surface actually occupies. A field is a surface and not a
 * solid: it stops what lands on it and there is nothing underneath it, which is the one way it is
 * not a box.
 */
export function heightfieldCollider(options: HeightfieldOptions): ColliderSpec {
  const { samples } = options;
  if (samples.samplesX < 2 || samples.samplesZ < 2) {
    throw new RangeError(`heightfieldCollider: '${options.id}' needs two samples on each axis`);
  }
  if (samples.heights.length !== samples.samplesX * samples.samplesZ) {
    throw new RangeError(`heightfieldCollider: '${options.id}' has the wrong sample count`);
  }

  const range = fieldRange(samples);
  // Never zero, so a flat field still has a box to be culled by.
  const halfHeight = Math.max(1e-3, (range.max - range.min) / 2);

  return {
    id: options.id,
    shape: 'heightfield',
    center: vec3(options.center.x, options.center.y, options.center.z),
    halfExtents: vec3(options.halfWidth, halfHeight, options.halfDepth),
    samples,
    yaw: 0,
    tilt: 0,
    visible: options.visible ?? false,
    impactSurface: options.impactSurface,
  };
}

/**
 * The floor, a box thick enough that nothing tunnels through it at any step length. The one helper
 * that names its own surface, because dirt is what a floor is until a level says otherwise.
 */
export function groundCollider(
  id: string,
  halfExtents: Vec3,
  thickness = 1,
  impactSurface: ImpactSurfaceId = 'packedDirt',
): ColliderSpec {
  return boxCollider({
    id,
    center: vec3(0, -thickness / 2, 0),
    halfExtents: vec3(halfExtents.x, thickness / 2, halfExtents.z),
    visible: false,
    impactSurface,
  });
}
